import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from "./ui/dropdown-menu";
import { ChevronDown, LogOut, Briefcase, Boxes, ShieldCheck, Check } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * Unified avatar dropdown — identity + workspace switcher + sign out.
 *
 * - Workspace section renders ONLY when the user has BOTH verticals. Single-
 *   vertical users see identity + sign out only, with zero reference to the
 *   other workspace (Part 1 access boundary preserved exactly).
 * - Current workspace is inferred from the URL: `/inventory*` → Inventory,
 *   everything else → Finance. One click jumps to that vertical's home.
 */
export default function UserMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  if (!user) return null;

  const isAdmin = user.role === "admin";
  const verticals = user.verticals || ["finance"];
  const hasFinance = isAdmin || verticals.includes("finance");
  const hasInventory = isAdmin || verticals.includes("inventory");
  const hasLicenses = isAdmin || verticals.includes("licenses");
  const workspaceCount = [hasFinance, hasInventory, hasLicenses].filter(Boolean).length;
  const showSwitcher = workspaceCount >= 2;

  const active = location.pathname.startsWith("/inventory")
    ? "inventory"
    : location.pathname.startsWith("/licenses")
      ? "licenses"
      : "finance";

  const goTo = (vertical) => {
    if (vertical === "inventory") navigate("/inventory");
    else if (vertical === "licenses") navigate("/licenses");
    else navigate("/dashboard");
  };

  const initial = (user.name || user.email || "?").charAt(0).toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="user-menu-trigger"
          className="flex items-center gap-1.5 rounded-full pl-1 pr-2 py-1 hover:bg-slate-50 transition-colors"
          aria-label="Open user menu"
        >
          <div className="w-8 h-8 rounded-full bg-olive text-white grid place-items-center text-sm font-semibold">
            {initial}
          </div>
          <ChevronDown size={13} strokeWidth={2} className="text-slate-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="w-72 rounded-2xl border-slate-200 p-1.5"
        data-testid="user-menu-content"
      >
        {/* Identity */}
        <div className="px-3 py-2.5" data-testid="user-menu-identity">
          <div className="text-sm font-semibold text-slate-900 truncate">{user.name || user.email}</div>
          {user.name && <div className="text-[11px] text-slate-500 truncate">{user.email}</div>}
          <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold uppercase tracking-wider">
            {user.role}
          </span>
        </div>

        {showSwitcher && (
          <>
            <DropdownMenuSeparator className="my-1" />
            <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 px-3 pt-2 pb-1">
              Workspace
            </DropdownMenuLabel>
            <div data-testid="user-menu-workspace-switcher" className="space-y-1 px-1">
              {hasFinance && (
                <WorkspaceRow
                  testid="workspace-row-finance"
                  label="Finance"
                  desc="Requests · approvals · payments · reports"
                  Icon={Briefcase}
                  active={active === "finance"}
                  onClick={() => goTo("finance")}
                />
              )}
              {hasInventory && (
                <WorkspaceRow
                  testid="workspace-row-inventory"
                  label="Inventory"
                  desc="Stock · transfers · breakage · valuation"
                  Icon={Boxes}
                  active={active === "inventory"}
                  onClick={() => goTo("inventory")}
                />
              )}
              {hasLicenses && (
                <WorkspaceRow
                  testid="workspace-row-licenses"
                  label="Licenses"
                  desc="Registry · expiry · renewal reminders"
                  Icon={ShieldCheck}
                  active={active === "licenses"}
                  onClick={() => goTo("licenses")}
                />
              )}
            </div>
          </>
        )}

        <DropdownMenuSeparator className="my-1" />
        <DropdownMenuItem
          data-testid="user-menu-logout"
          onClick={logout}
          className="rounded-xl cursor-pointer text-slate-700 focus:bg-slate-100 focus:text-slate-900 px-3 py-2 text-sm font-medium gap-2"
        >
          <LogOut size={14} strokeWidth={1.9} />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const WorkspaceRow = ({ testid, label, desc, Icon, active, onClick }) => (
  <button
    data-testid={testid}
    data-active={active ? "true" : "false"}
    onClick={onClick}
    className={
      "w-full text-left rounded-xl border px-3 py-2.5 flex items-center gap-2.5 transition-colors " +
      (active
        ? "bg-olive/5 border-olive/40"
        : "bg-white border-slate-100 hover:bg-slate-50 hover:border-slate-200")
    }
  >
    <div
      className={
        "w-9 h-9 rounded-xl grid place-items-center shrink-0 " +
        (active ? "bg-olive/15 text-olive" : "bg-slate-100 text-slate-500")
      }
    >
      <Icon size={15} strokeWidth={1.8} />
    </div>
    <div className="flex-1 min-w-0">
      <div className={"text-sm font-semibold " + (active ? "text-olive" : "text-slate-900")}>
        {label}
        {active && (
          <span data-testid={`${testid}-current-marker`} className="ml-1.5 text-[10px] font-semibold uppercase tracking-wider text-olive/80">
            · Current
          </span>
        )}
      </div>
      <div className="text-[11px] text-slate-500 truncate mt-0.5">{desc}</div>
    </div>
    {active && (
      <Check size={14} strokeWidth={2.2} className="text-olive shrink-0" />
    )}
  </button>
);
